import type { EditorDocument } from "./types";

/**
 * Deep equality of JSON values. Key order does not matter, and a key whose value is `undefined` is
 * the same as no key, which is how it would be stored.
 */
export function jsonEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((value, index) => jsonEqual(value, b[index]));
  }
  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const keys = Object.keys(left).filter((key) => left[key] !== undefined);
  const others = Object.keys(right).filter((key) => right[key] !== undefined);
  if (keys.length !== others.length) return false;
  return keys.every((key) => Object.hasOwn(right, key) && jsonEqual(left[key], right[key]));
}

/**
 * Whether two documents hold the same content. The envelope around it (schema and engine versions,
 * and whatever revision a repository keeps beside it) is metadata, and is not compared.
 */
export const sameContent = (a: EditorDocument, b: EditorDocument): boolean =>
  jsonEqual(a.content, b.content);
